import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { services } from '@/constants/content'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'

export const Approach: FC = (): ReactNode => {
    return (
        <section
            id='services'
            className='edge grain bg-[color:var(--ink)] py-20 text-[color:var(--cream)] md:py-28'
            aria-labelledby='services-heading'
        >
            <div className='mx-auto grid max-w-[110rem] gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <SplitText
                        as='h2'
                        lines={['What I can', 'offer']}
                        className='display-xl'
                    />

                    <span id='services-heading' className='sr-only'>
                        Services
                    </span>

                    <Reveal delay={0.1}>
                        <p className='mt-6 max-w-sm text-[0.9375rem] leading-relaxed text-white/60'>
                            A range of photography services covering the moments that matter
                            most, from a single headshot through to a wedding day covered
                            end to end.
                        </p>

                        <Link
                            href='/portfolio'
                            className='pill mt-9 border border-white/20 hover:bg-white hover:text-[color:var(--ink)]'
                        >
                            Check out my work
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </Reveal>
                </div>

                <ul className='grid gap-3 sm:grid-cols-2'>
                    {services.map((service, index) => (
                        <Reveal
                            key={service.title}
                            as='li'
                            delay={index * 0.06}
                            y={34}
                            className='group rounded-[var(--radius-lg)] border border-white/10 bg-white/[0.03] p-6 transition-colors duration-500 hover:border-white/25 hover:bg-white/[0.06]'
                        >
                            <span className='relative block size-14 overflow-hidden rounded-[calc(var(--radius-lg)*0.6)]'>
                                <Image
                                    src={service.image}
                                    alt=''
                                    fill
                                    sizes='56px'
                                    className='object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110'
                                />
                            </span>

                            <h3 className='mt-6 text-[0.9375rem] font-medium'>{service.title}</h3>

                            <p className='mt-2 text-[0.8125rem] leading-relaxed text-white/50'>
                                {service.blurb}
                            </p>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>
    )
}
